/* jp-smart-search.js — understand what was typed before searching for it.

   "ovl emrald rngs", "Emerald OVAL ring" and "oval emerald rings" all mean the
   same thing. This reads the query, fixes the spelling against the words a
   jewellery shop actually uses, and picks out the stone SHAPE and the stone it
   belongs to:

     "ovl emrald rngs"  ->  text "oval emerald ring"
                            shape Oval, bound to Emerald
                            filter: jewelry.shaped_stones = Oval Emerald

   Why the filter: Shopify's search quietly drops any word it cannot place, so
   before this "marquise ruby pendant" listed every ruby pendant. The shape now
   lives on each product (sync_shapes.py writes it from the ERP stone remarks)
   and Search & Discovery filters on it, which makes a shape search exact.
   "Oval Emerald" means the emerald itself is oval, not merely that the piece
   has an emerald and some oval stone.

   If nothing matches exactly the search widens one step at a time — any stone
   of that shape, then no shape at all — and says so, instead of showing an
   empty page.

   Two modes, told apart by data-india on the script tag:
     normal  drives the search popup and rewrites /search requests
     India   only exposes window.JPSearch.parse; jp-india-search.js uses it
             against the local index, and no Shopify search request is made.
*/
(function () {
  'use strict';

  var INDIA = !!(document.currentScript && document.currentScript.dataset.india === '1');

  /* ── Vocabulary ─────────────────────────────────────────────────────────── */

  // Canonical shape -> phrases that mean it. Multi-word phrases win over single
  // words, which is what lets "emerald cut" be a shape and not a stone.
  var SHAPES = {
    'Oval':     ['oval', 'oval cut', 'oval shape', 'oval shaped'],
    'Pear':     ['pear', 'pear cut', 'pear shape', 'pear shaped', 'teardrop', 'tear drop',
                 'tear-drop', 'tear shaped'],
    'Octagon':  ['octagon', 'octagonal', 'octagon cut', 'emerald cut', 'emerald-cut',
                 'step cut', 'rectangle', 'rectangular', 'octagon shape'],
    'Heart':    ['heart', 'heart cut', 'heart shape', 'heart shaped', 'heart-shaped'],
    'Round':    ['round', 'round cut', 'round brilliant', 'round shape', 'brilliant cut',
                 'circle', 'circular'],
    'Marquise': ['marquise', 'marquis', 'marquise cut', 'navette', 'marquee', 'marquise shape'],
    'Cushion':  ['cushion', 'cushion cut', 'cushion shape'],
    // Added 2026-09-21 with the ERP's newer stone remarks. Pave, Letter and
    // Wave are not cuts of a stone, but they live in the same field and are
    // what a customer types, so they behave like the rest.
    'Baguette': ['baguette', 'baguettes', 'baguette cut', 'bagette'],
    'Princess': ['princess', 'princess cut', 'princess shape'],
    'Rose Cut': ['rose cut', 'rosecut', 'rose-cut'],
    'Cabochon': ['cabochon', 'cabochon cut', 'cab', 'cabachon'],
    'Pave':     ['pave', 'pavé', 'pave set', 'pavé set', 'paved'],
    'Letter':   ['letter', 'letters', 'initial', 'initials', 'alphabet'],
    'Wave':     ['wave', 'waves', 'wavy']
  };

  // Label -> [phrases], [stone names as written on the products]. The values
  // are what sync_shapes.py pairs with a shape, so "Oval" + "Sapphire" becomes
  // every sapphire colour the shop carries.
  var GEMS = [
    ['Emerald', ['emerald', 'emeralds'], ['Emerald', 'Colombian Emerald']],
    ['Colombian Emerald', ['colombian emerald', 'columbian emerald'], ['Colombian Emerald']],
    ['Ruby', ['ruby', 'rubies'], ['Ruby']],
    ['Diamond', ['diamond', 'diamonds'],
      ['Diamond', 'Lab Grown Diamond', 'Yellow Diamond', 'Pink Diamond']],
    ['Natural Diamond', ['natural diamond', 'real diamond', 'mined diamond'], ['Diamond']],
    ['Lab Grown Diamond', ['lab grown diamond', 'lab diamond', 'lab grown', 'lab created diamond',
      'lab-grown diamond', 'lab-grown', 'cvd', 'cvd diamond', 'lgd'], ['Lab Grown Diamond']],
    ['Yellow Diamond', ['yellow diamond', 'canary diamond'], ['Yellow Diamond']],
    ['Pink Diamond', ['pink diamond'], ['Pink Diamond']],
    // In the trade a sapphire is a blue one — the colour is named only when it
    // is not blue — so this matches the filter on the collection pages. Pink,
    // yellow, orange and rainbow are asked for by name.
    ['Sapphire', ['sapphire', 'sapphires'], ['Blue Sapphire']],
    ['Blue Sapphire', ['blue sapphire'], ['Blue Sapphire']],
    ['Pink Sapphire', ['pink sapphire'], ['Pink Sapphire']],
    ['Yellow Sapphire', ['yellow sapphire'], ['Yellow Sapphire']],
    ['Orange Sapphire', ['orange sapphire', 'padparadscha'], ['Orange Sapphire']],
    ['Rainbow Sapphire', ['rainbow sapphire'], ['Rainbow Sapphire']],
    ['Rainbow Gemstone', ['rainbow gemstone', 'rainbow gem', 'rainbow stone'], ['Rainbow Gemstone']],
    ['Turquoise', ['turquoise'], ['Turquoise']],
    ['Aquamarine', ['aquamarine', 'aqua marine', 'aqua'], ['Aquamarine']],
    ['Coral', ['coral'], ['Coral']],
    ['Paraiba Tourmaline', ['paraiba tourmaline', 'paraiba', 'paraíba'], ['Paraiba Tourmaline']],
    ['Tourmaline', ['tourmaline'], ['Paraiba Tourmaline', 'Rubellite', 'Tourmaline Mix']],
    ['Rubellite', ['rubellite', 'rubelite', 'rubillite', 'pink tourmaline'], ['Rubellite']],
    ['Kunzite', ['kunzite'], ['Kunzite']],
    ['Tanzanite', ['tanzanite'], ['Tanzanite']],
    ['Amethyst', ['amethyst'], ['Amethyst']],
    ['Spinel', ['spinel', 'spinal'], ['Spinel']],
    ['Opal', ['opal', 'opals'], ['Opal']],
    ['Peridot', ['peridot'], ['Peridot']],
    ['Tsavorite', ['tsavorite', 'tsavorite garnet'], ['Tsavorite']],
    ['Garnet', ['garnet', 'garnets'], ['Garnet', 'Tsavorite']],
    ['Topaz', ['topaz'], ['Blue Topaz', 'London Blue Topaz', 'Swiss Blue Topaz']],
    ['Blue Topaz', ['blue topaz'], ['Blue Topaz', 'London Blue Topaz', 'Swiss Blue Topaz']],
    ['London Blue Topaz', ['london blue topaz', 'london blue', 'lbt'], ['London Blue Topaz']],
    ['Swiss Blue Topaz', ['swiss blue topaz', 'swiss blue'], ['Swiss Blue Topaz']],
    ['Citrine', ['citrine'], ['Citrine']],
    ['Quartz', ['quartz', 'lavender quartz'], ['Lavender Quartz']],
    ['Golden Rutile', ['golden rutile', 'rutile'], ['Golden Rutile']],
    ['Pearl', ['pearl', 'pearls'], []]
  ];

  // Item words: corrected, then kept in the text query as they are.
  var ITEMS = {
    'ring': ['ring', 'rings', 'band', 'bands'],
    'earrings': ['earring', 'earrings', 'ear ring', 'ear rings'],
    'studs': ['stud', 'studs'],
    'hoops': ['hoop', 'hoops'],
    'huggies': ['huggie', 'huggies', 'huggy'],
    'pendant': ['pendant', 'pendants'],
    'necklace': ['necklace', 'necklaces'],
    'bracelet': ['bracelet', 'bracelets'],
    'bangle': ['bangle', 'bangles'],
    'chain': ['chain', 'chains']
  };

  // Item -> the product types it may be (the store's Product type field,
  // filtered on through filter.p.product_type). "chain" has no type of its
  // own, so it stays a word in the text.
  var ITEM_TYPES = {
    'ring': ['Ring'],
    'earrings': ['Earrings', 'Earring'],
    'studs': ['Earrings', 'Earring'],
    'hoops': ['Earrings', 'Earring'],
    'huggies': ['Earrings', 'Earring'],
    'pendant': ['Pendant'],
    'necklace': ['Necklace'],
    'bracelet': ['Bracelet'],
    'bangle': ['Bracelet']
  };

  // The word each item is sent to Shopify as. Singular and short on purpose:
  // the last word is matched as the start of a word, so "ring" finds "Ring"
  // and "Rings" while "rings" would miss every title that says "Ring".
  var ITEM_TEXT = {
    'ring': 'ring', 'earrings': 'earring', 'studs': 'stud', 'hoops': 'hoop',
    'huggies': 'huggie', 'pendant': 'pendant', 'necklace': 'necklace',
    'bracelet': 'bracelet', 'bangle': 'bangle', 'chain': 'chain'
  };
  // Same for stones: plurals and synonyms become the name on the products.
  var GEM_TEXT = { 'Natural Diamond': 'diamond', 'Quartz': 'quartz' };

  // Everything else worth spell-checking. Words not in any list are passed
  // through untouched — a design name the shop uses but this file doesn't
  // know still reaches Shopify exactly as typed.
  var WORDS = ['gold', 'yellow', 'white', 'rose', 'pink', 'blue', 'green', 'red', 'black',
    'platinum', 'silver', '14k', '18k', 'pinky', 'chunky', 'signet', 'tennis', 'halo',
    'eternity', 'cluster', 'bezel', 'solitaire', 'three', 'stone', 'cocktail', 'statement',
    'dangle', 'drop', 'charm', 'link', 'cuban', 'ready', 'ship', 'gemstone', 'gemstones',
    'rainbow', 'natural', 'vintage', 'engagement', 'wedding', 'anniversary', 'promise',
    'stackable', 'minimalist', 'dainty', 'bold', 'open', 'adjustable', 'baguette',
    'princess', 'trillion', 'emerald', 'with', 'and', 'lab', 'grown', 'shape', 'shaped', 'cut'];

  // Words that only say "this is a shape" and add nothing once the shape is known.
  var SHAPE_FILLER = { 'shape': 1, 'shaped': 1, 'cut': 1, 'stone': 1 };

  /* ── Lookup tables built from the vocabulary ─────────────────────────────── */

  var PHRASES = {};      // phrase -> {kind, value}
  var VOCAB = {};        // single word -> true (the spell-check dictionary)

  function addPhrase(p, entry) {
    PHRASES[p] = entry;
    p.split(/[\s-]+/).forEach(function (w) { if (w) VOCAB[w] = true; });
  }
  Object.keys(SHAPES).forEach(function (s) {
    SHAPES[s].forEach(function (p) { addPhrase(p, { kind: 'shape', value: s }); });
  });
  GEMS.forEach(function (g) {
    g[1].forEach(function (p) { addPhrase(p, { kind: 'gem', value: g[0], stones: g[2] }); });
  });
  Object.keys(ITEMS).forEach(function (it) {
    ITEMS[it].forEach(function (p) { addPhrase(p, { kind: 'item', value: it }); });
  });
  WORDS.forEach(function (w) { VOCAB[w] = true; });
  var VOCAB_LIST = Object.keys(VOCAB);

  /* ── Spelling ────────────────────────────────────────────────────────────── */

  // Optimal-string-alignment distance: Levenshtein plus adjacent swaps, so
  // "emrlad" is one edit from "emerald"'s neighbour rather than two.
  function distance(a, b, max) {
    if (Math.abs(a.length - b.length) > max) return max + 1;
    var d = [], i, j;
    for (i = 0; i <= a.length; i++) { d[i] = [i]; }
    for (j = 0; j <= b.length; j++) { d[0][j] = j; }
    for (i = 1; i <= a.length; i++) {
      var rowMin = max + 1;
      for (j = 1; j <= b.length; j++) {
        var cost = a[i - 1] === b[j - 1] ? 0 : 1;
        d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
        if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
          d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
        }
        if (d[i][j] < rowMin) rowMin = d[i][j];
      }
      if (rowMin > max) return max + 1;
    }
    return d[a.length][b.length];
  }

  function allowed(len) { return len <= 3 ? 1 : len <= 5 ? 1 : len <= 8 ? 2 : 3; }

  var cache = {};
  // Best dictionary word for w, or w itself. `partial` is true for the word
  // still being typed: there a prefix of a known word is left alone, so "emer"
  // is not "corrected" to something else before the visitor finishes it.
  function correct(w, partial) {
    if (VOCAB[w] || /\d/.test(w) || w.length < 3) return w;
    // plurals and simple suffixes
    var base = w.replace(/ies$/, 'y').replace(/(es|s)$/, '');
    if (VOCAB[base]) return base;
    if (VOCAB[w.replace(/s$/, '')]) return w.replace(/s$/, '');
    if (partial) {
      for (var k = 0; k < VOCAB_LIST.length; k++) {
        if (VOCAB_LIST[k].indexOf(w) === 0) return w;
      }
    }
    var key = w + (partial ? '|p' : '');
    if (key in cache) return cache[key];
    // A typo and a plural together ("rngs"): try the word without its "s" too.
    var tries = w.length > 4 && /s$/.test(w) ? [w, w.slice(0, -1)] : (/s$/.test(w) ? [w.slice(0, -1), w] : [w]);
    var best = w, bestD = 99;
    tries.forEach(function (t) {
      var lim = allowed(t.length);
      for (var i = 0; i < VOCAB_LIST.length; i++) {
        var v = VOCAB_LIST[i];
        if (v.length < 3) continue;
        // Three-letter input only corrects to a word with the same first
        // letter: "ovl" is oval, but "old" must not become "gold".
        if (t.length <= 3 && v[0] !== t[0]) continue;
        var dd = distance(t, v, lim);
        if (dd > lim) continue;
        // On a tie, prefer the word that starts with the same letter.
        if (dd < bestD || (dd === bestD && v[0] === w[0] && best[0] !== w[0])) {
          best = v; bestD = dd;
        }
      }
    });
    cache[key] = best;
    return best;
  }

  /* ── Parse ───────────────────────────────────────────────────────────────── */

  // Two words typed without the space between them ("ovalemerald").
  function split(w) {
    if (VOCAB[w] || w.length < 7) return [w];
    for (var i = 3; i <= w.length - 3; i++) {
      var a = w.slice(0, i), b = w.slice(i);
      if (VOCAB[a] && VOCAB[b]) return [a, b];
    }
    return [w];
  }

  function tokenize(q) {
    var out = [];
    String(q || '').toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9À-ɏ\s-]/g, ' ')
      .replace(/-/g, ' ')
      .split(/\s+/).filter(Boolean)
      .forEach(function (w) { out = out.concat(split(w)); });
    return out;
  }

  /* Returns
       raw        what was typed
       words      corrected words, in order
       corrected  true when spelling was changed (not just case or plural)
       shapes     [{shape, gem}]  — gem is the stone the shape is bound to, or null
       gems       [{label, stones}]
       text       the query to send to Shopify
       filter     {key, values} or null — the exact filter, see filterFor()  */
  function parse(q, opts) {
    opts = opts || {};
    var raw = String(q || '').trim();
    var toks = tokenize(raw);
    var endsWithSpace = /\s$/.test(String(q || ''));
    var fixed = toks.map(function (t, i) {
      return correct(t, opts.typing && i === toks.length - 1 && !endsWithSpace);
    });
    var corrected = fixed.some(function (w, i) {
      var t = toks[i];
      return w !== t && w !== t.replace(/s$/, '') && w !== t.replace(/ies$/, 'y') &&
             w !== t.replace(/es$/, '');
    });

    // Longest phrase first, left to right.
    var spans = [];
    for (var i = 0; i < fixed.length;) {
      var hit = null;
      for (var n = Math.min(4, fixed.length - i); n >= 1 && !hit; n--) {
        var ph = fixed.slice(i, i + n).join(' ');
        if (PHRASES[ph]) hit = { n: n, entry: PHRASES[ph], text: ph, at: i };
      }
      if (hit) { spans.push(hit); i += hit.n; }
      else { spans.push({ n: 1, entry: null, text: fixed[i], at: i }); i += 1; }
    }

    var shapeSpans = spans.filter(function (s) { return s.entry && s.entry.kind === 'shape'; });
    var gemSpans = spans.filter(function (s) { return s.entry && s.entry.kind === 'gem'; });

    // Bind each shape to the nearest stone: the one right after it ("oval
    // emerald") first, then the one right before it ("emerald oval ring").
    var used = {};
    var shapes = shapeSpans.map(function (s) {
      var best = null, bestGap = 99;
      gemSpans.forEach(function (g) {
        if (used[g.at]) return;
        var gap = g.at > s.at ? g.at - (s.at + s.n) : (s.at - (g.at + g.n)) + 0.5;
        if (gap >= 0 && gap < bestGap && gap <= 2.5) { best = g; bestGap = gap; }
      });
      if (best) used[best.at] = true;
      return { shape: s.entry.value, gem: best ? { label: best.entry.value, stones: best.entry.stones } : null };
    });
    var gems = gemSpans.map(function (g) { return { label: g.entry.value, stones: g.entry.stones }; });
    var items = [];
    spans.forEach(function (s) {
      if (!s.entry || s.entry.kind !== 'item' || !ITEM_TYPES[s.entry.value]) return;
      if (!items.some(function (x) { return x.item === s.entry.value; })) {
        items.push({ item: s.entry.value, types: ITEM_TYPES[s.entry.value] });
      }
    });

    // The text Shopify gets: the corrected words, with shape synonyms written
    // as the shape's own name (the tag on the product) and filler dropped.
    var hasShape = shapeSpans.length > 0;
    var text = spans.map(function (s) {
      if (s.entry && s.entry.kind === 'shape') return s.entry.value.toLowerCase();
      if (s.entry && s.entry.kind === 'item') return ITEM_TEXT[s.entry.value] || s.text;
      if (s.entry && s.entry.kind === 'gem') return GEM_TEXT[s.entry.value] || s.entry.value.toLowerCase();
      if (!s.entry && hasShape && SHAPE_FILLER[s.text]) return '';
      return s.text;
    }).filter(Boolean).join(' ');

    return {
      raw: raw,
      words: fixed,
      corrected: corrected,
      shapes: shapes,
      gems: gems,
      items: items,
      text: text,
      label: labelFor(shapes, spans)
    };
  }

  function titleCase(s) {
    return s.replace(/\b[a-z]/g, function (c) { return c.toUpperCase(); });
  }

  // Human label for the chip: "Oval Emerald", "Pear" ...
  function labelFor(shapes) {
    return shapes.map(function (s) { return s.gem ? s.shape + ' ' + s.gem.label : s.shape; }).join(', ');
  }

  /* The exact filter at a given strictness:
       0  every shape paired with its stone   jewelry.shaped_stones
       1  the shapes alone                    jewelry.stone_shape
       2  none                                                          */
  function filterFor(p, level) {
    if (!p.shapes.length || level >= 2) return null;
    if (level === 0 && p.shapes.every(function (s) { return s.gem && s.gem.stones.length; })) {
      var vals = [];
      p.shapes.forEach(function (s) {
        s.gem.stones.forEach(function (st) {
          var v = s.shape + ' ' + st;
          if (vals.indexOf(v) === -1) vals.push(v);
        });
      });
      return { key: 'filter.p.m.jewelry.shaped_stones', values: vals, level: 0 };
    }
    var shapesOnly = [];
    p.shapes.forEach(function (s) { if (shapesOnly.indexOf(s.shape) === -1) shapesOnly.push(s.shape); });
    return { key: 'filter.p.m.jewelry.stone_shape', values: shapesOnly, level: 1 };
  }

  // Does one product (with its shape lists) satisfy the filter? Used where the
  // browser has the data itself: the India index and the popup's safety check.
  function matches(filter, shapeList, pairList) {
    if (!filter) return true;
    var have = filter.level === 0 ? pairList : shapeList;
    have = (have || []).map(function (x) { return String(x).toLowerCase(); });
    return filter.values.some(function (v) { return have.indexOf(v.toLowerCase()) !== -1; });
  }

  /* Every filter at a given strictness, strictest first:
       0  shaped stone ("Oval Emerald")  + item type
       1  shape alone  ("Oval")          + item type
       2                                   item type
       3  nothing
     The item type is kept longest because it is the thing asked for: an
     "oval ruby pendant" search that finds no oval ruby should offer other
     pendants, not oval rubies set in earrings. */
  function levelFilters(p, level) {
    var out = [];
    var f = filterFor(p, Math.min(level, 2));
    if (f) out.push(f);
    if (level <= 2 && p.items.length) {
      var types = [];
      p.items.forEach(function (it) {
        it.types.forEach(function (t) { if (types.indexOf(t) === -1) types.push(t); });
      });
      out.push({ key: 'filter.p.product_type', values: types, level: 'item' });
    }
    return out;
  }
  function signature(p, level) {
    return JSON.stringify(levelFilters(p, level).map(function (f) { return [f.key, f.values]; }));
  }
  // The next strictness that actually changes something, or -1.
  function nextLevel(p, level) {
    var here = signature(p, level);
    for (var l = level + 1; l <= 3; l++) {
      if (signature(p, l) !== here) return l;
    }
    return -1;
  }

  /* ── URLs ────────────────────────────────────────────────────────────────── */

  var FIELDS = 'title,product_type,variants.title,variants.sku,tag';

  /* Level 1 means "that shape, any stone", so the stone the shape was bound to
     has to leave the words as well — otherwise the text still demands it and
     the widened filter finds nothing ("rose cut diamond": the rose cuts are
     sapphires). Level 2 drops the shape instead, so the stone comes back. */
  function textFor(p, level) {
    var text = p.text || p.raw;
    if (level !== 1) return text;
    var drop = {};
    p.shapes.forEach(function (s) {
      if (s.gem) s.gem.label.toLowerCase().split(' ').forEach(function (w) { drop[w] = 1; });
    });
    var words = text.split(' ').filter(function (w) { return !drop[w]; });
    return words.length ? words.join(' ') : text;
  }

  function searchParams(p, level, extra) {
    var sp = new URLSearchParams();
    sp.set('q', textFor(p, level));
    sp.set('type', 'product');
    sp.set('options[prefix]', 'last');
    sp.set('options[unavailable_products]', 'last');
    sp.set('options[fields]', FIELDS);
    levelFilters(p, level).forEach(function (f) {
      f.values.forEach(function (v) { sp.append(f.key, v); });
    });
    sp.set('jp', String(level + 1));
    if (p.corrected && p.raw) sp.set('jp_q', p.raw);
    if (extra) Object.keys(extra).forEach(function (k) { sp.set(k, extra[k]); });
    return sp;
  }

  function resultsUrl(p, level, page) {
    var sp = searchParams(p, level || 0);
    if (page > 1) sp.set('page', String(page));
    return '/search?' + sp.toString();
  }

  // What to tell the visitor when the search had to widen.
  function relaxNote(p, level, shown) {
    if (level === 0 || shown === 0) return '';
    if (level === 3) {
      return p.items.length || p.shapes.length
        ? 'Nothing matched exactly — showing the closest pieces.' : '';
    }
    if (!p.shapes.length) return '';
    var pairs = p.shapes.filter(function (s) { return s.gem; })
      .map(function (s) { return s.shape.toLowerCase() + ' ' + s.gem.label.toLowerCase(); });
    if (level === 1 && pairs.length) {
      return 'No ' + pairs.join(' or ') + ' pieces right now — showing every ' +
        p.shapes.map(function (s) { return s.shape.toLowerCase(); }).join(' or ') + ' stone.';
    }
    if (level === 2) {
      return 'No ' + p.shapes.map(function (s) { return s.shape.toLowerCase(); }).join(' or ') +
        ' stones match — showing the closest pieces.';
    }
    return '';
  }

  window.JPSearch = {
    parse: parse,
    filterFor: filterFor,
    matches: matches,
    levelFilters: levelFilters,
    nextLevel: nextLevel,
    resultsUrl: resultsUrl,
    relaxNote: relaxNote,
    india: INDIA
  };

  if (INDIA) return;   // India: nothing below may make a Shopify search request.

  /* ── Popup ───────────────────────────────────────────────────────────────── */

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // Same markup the theme's own predictive search produced, so the popup's
  // existing styles apply unchanged.
  // One width for every popup thumbnail: Shopify renders a size on demand and
  // the first request for a new one is slow, so asking for the same 400 the
  // feed already uses keeps them warm in the CDN.
  function withWidth(u) {
    if (!u) return u;
    return /[?&]width=/.test(u) ? u : u + (u.indexOf('?') === -1 ? '?' : '&') + 'width=400';
  }

  function card(pr) {
    var img = pr.i ? '<img src="' + esc(withWidth(pr.i)) + '" alt="' + esc(pr.t) +
      '" loading="' + (pr.__eager ? 'eager' : 'lazy') + '" decoding="async">' : '';
    var sold = pr.a ? '' : '<span class="flex prod__tag prod__tag--soldout">Sold Out</span>';
    var cmp = pr.c ? '<span class="sf-currency text-color-secondary text-sm line-through mr-2">' + esc(pr.c) + '</span>' : '';
    return '<div data-search-item><a href="' + esc(pr.u) + '" class="mb-3 flex flex-col group">' +
      '<div class="mb-3 w-full">' + img + sold + '</div>' +
      '<div class="md:ml-0 w-full"><p class="font-medium">' + esc(pr.t) + '</p>' +
      '<div class="mt-1"><span class="text-color-sale-price">' + cmp + '</span>' +
      '<span class="text-color-regular-price"><span class="sf-currency">' + esc(pr.p) + '</span></span></div></div>' +
      '</a></div>';
  }

  /* Two sources, because they trade speed against exactness:

       /search/suggest.json   ~0.2s, ten products, with their tags and type.
                              Shape and item are enforced here in the browser
                              against the tags sync_shapes.py writes.
       /search?view=jp-smart  ~0.8s (every keystroke is a new query, so it is
                              rarely a warm one), but it is the real search
                              with the real filters and the real count.

     The popup shows the fast one as soon as it lands and refreshes with the
     exact one when it arrives, so typing feels immediate without the list
     settling on something the results page would disagree with. */
  /* Kept for the visit, not just the page: Shopify answers a search in about
     0.7s however often it is asked, so a query typed twice — or typed again on
     the next page — should not pay for it twice. */
  var SS_KEY = 'jp_search_cache_v1';
  var ssCache = (function () {
    try { return JSON.parse(sessionStorage.getItem(SS_KEY)) || {}; } catch (e) { return {}; }
  })();
  function ssSave(key, val) {
    try {
      ssCache[key] = val;
      var keys = Object.keys(ssCache);
      // Keep it small; the oldest entries are the least likely to be retyped.
      while (keys.length > 40) delete ssCache[keys.shift()];
      sessionStorage.setItem(SS_KEY, JSON.stringify(ssCache));
    } catch (e) {}
  }

  var suggestCache = {};
  function suggest(p) {
    var sp = new URLSearchParams();
    sp.set('q', p.text || p.raw);
    sp.set('resources[type]', 'product');
    sp.set('resources[limit]', '10');
    sp.set('resources[options][fields]', FIELDS);
    var url = '/search/suggest.json?' + sp.toString();
    if (suggestCache[url]) return suggestCache[url];
    if (ssCache[url]) {
      suggestCache[url] = Promise.resolve(ssCache[url]);
      return suggestCache[url];
    }
    suggestCache[url] = fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        var list = (((d || {}).resources || {}).results || {}).products || [];
        return list.map(function (x) {
          return {
            t: x.title, u: x.url,
            i: x.featured_image && x.featured_image.url ? x.featured_image.url : x.image,
            p: money(x.price), c: Number(x.compare_at_price_min) > Number(x.price_min)
              ? money(x.compare_at_price_min) : null,
            a: x.available !== false, tags: x.tags || [], type: x.type || ''
          };
        });
      })
      .then(function (list) { ssSave(url, list); return list; })
      .catch(function () { delete suggestCache[url]; return []; });
    return suggestCache[url];
  }

  // Suggest gives prices as plain numbers; the feed gives them formatted.
  function money(v) {
    var n = Number(v);
    if (!isFinite(n)) return '';
    return '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // Same narrowing as the server filters, done on what suggest returned.
  function narrow(list, p, level) {
    var f = filterFor(p, Math.min(level, 2));
    var items = level <= 2 ? p.items : [];
    return list.filter(function (x) {
      if (f && !matches(f, x.tags, x.tags)) return false;
      if (items.length && x.type) {
        var ok = items.some(function (it) {
          return it.types.some(function (t) { return t.toLowerCase() === x.type.toLowerCase(); });
        });
        if (!ok) return false;
      }
      return true;
    });
  }

  // Strictest level with anything in it, using only what suggest returned.
  function bestQuick(p) {
    return suggest(p).then(function (list) {
      for (var level = 0; level <= 3; level++) {
        var hits = narrow(list, p, level);
        if (hits.length || nextLevel(p, level) < 0) return { products: hits, level: level };
      }
      return { products: list, level: 3 };
    });
  }

  var feedCache = {};
  function feed(p, level) {
    var sp = searchParams(p, level, { view: 'jp-smart' });
    sp.delete('jp'); sp.delete('jp_q');
    var url = '/search?' + sp.toString();
    if (feedCache[url]) return feedCache[url];
    /* No Accept: application/json header — Shopify answers some storefront
       routes with a 404 when one is sent; the template returns JSON anyway. */
    feedCache[url] = fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : { products: [] }; })
      .then(function (d) {
        // If Search & Discovery is not filtering on the shape yet, the server
        // returns unfiltered results; narrow them here so the popup is still
        // right. The full results page needs the filter switched on.
        var f = filterFor(p, level);
        if (f && !d.filtered) {
          d.products = (d.products || []).filter(function (x) { return matches(f, x.h, x.s); });
        }
        return d;
      })
      .catch(function () { delete feedCache[url]; return { products: [] }; });
    return feedCache[url];
  }

  // Strictest level that returns anything.
  function bestFeed(p) {
    function tryLevel(level) {
      return feed(p, level).then(function (d) {
        var next = nextLevel(p, level);
        if ((d.products || []).length || next < 0) return { d: d, level: level };
        return tryLevel(next);
      });
    }
    return tryLevel(0);
  }

  function wirePopup() {
    var popup = document.querySelector('[data-search-popup]');
    if (!popup) return;
    var input = popup.querySelector('[data-search-input]');
    var form = input && input.form;
    var result = popup.querySelector('[data-result]');
    var wrap = popup.querySelector('[data-search-items-wrapper]');
    var msg = popup.querySelector('[data-message]');
    var qEl = popup.querySelector('[data-query]');
    var more = popup.querySelector('[data-more-result]');
    var popular = popup.querySelector('[data-popular-searches]');
    var spinner = popup.querySelector('[data-spinner]');
    var clear = popup.querySelector('[data-clear-search]');
    var submitBtn = form && form.querySelector('button[type="submit"]');
    var note = document.createElement('p');
    note.className = 'jp-ss-note';
    if (result && wrap) result.insertBefore(note, wrap);
    if (!input || !wrap || !result) return;
    wrap.innerHTML = '';

    var timer = null, seq = 0;
    function busy(on) {
      if (spinner) spinner.style.visibility = on ? 'visible' : 'hidden';
      if (submitBtn) submitBtn.style.visibility = on ? 'hidden' : 'visible';
    }
    function run() {
      var q = input.value;
      if (clear) clear.style.visibility = q.trim() ? 'visible' : 'hidden';
      if (!q.trim()) {
        result.classList.add('hidden');
        if (popular) popular.style.display = '';
        busy(false);
        return;
      }
      var mine = ++seq;
      var p = parse(q, { typing: true });
      busy(true);

      // Fast first, exact second. `stage` stops the slower answer from being
      // overtaken by its own fast one if they cross.
      var stage = 0;
      function show(list, level, count, exact) {
        if (mine !== seq || (!exact && stage > 0)) return;
        stage = exact ? 2 : 1;
        paint(list, level, count);
      }
      /* Both endpoints answer in about the same time from a browser, so
         asking for both on every keystroke only doubles the work. The exact
         one is fetched only when the fast answer looks thin — fewer than a
         full row, or it had to widen — which is exactly when the ten products
         suggest returns are too few to narrow from. */
      bestQuick(p).then(function (r) {
        show(r.products, r.level, r.products.length, false);
        if (r.products.length >= 5 && r.level === 0) return;
        bestFeed(p).then(function (res) {
          show(res.d.products || [], res.level, res.d.count, true);
        });
      });

      function paint(list, level, count) {
        busy(false);
        var shown = p.label && level === 0 ? titleCase(p.text) : (p.text || q.trim());
        if (qEl) qEl.textContent = shown;
        if (msg) msg.textContent = list.length
          ? (msg.dataset.resultsTitle || 'Results for')
          : (msg.dataset.noResults || 'No results for');
        note.textContent = relaxNote(p, level, list.length) || '';
        wrap.innerHTML = list.slice(0, 10).map(function (x, i) {
          x.__eager = i < 5;               // the first row is on screen already
          return card(x);
        }).join('');
        wrap.style.display = list.length ? '' : 'none';
        if (more) {
          more.href = resultsUrl(p, level);
          more.parentElement.style.display = (count || list.length) > 10 ? '' : 'none';
        }
        if (popular) popular.style.display = list.length ? 'none' : '';
        result.classList.remove('hidden');
      }
    }
    input.addEventListener('input', function () {
      clearTimeout(timer);
      timer = setTimeout(run, 140);
    });
    if (clear) clear.addEventListener('click', function () { seq++; busy(false); });

    /* The popular searches are one tap away and always the same, so they are
       fetched while the visitor is still reaching for the keyboard. */
    input.addEventListener('focus', function () {
      [].slice.call(popup.querySelectorAll('[data-ps-item]')).slice(0, 4).forEach(function (el) {
        suggest(parse(el.dataset.psQuery || el.textContent));
      });
    }, { once: true });
  }

  /* ── Every way into /search goes through the parser ──────────────────────── */

  function go(q) {
    if (!String(q || '').trim()) return;
    window.location.href = resultsUrl(parse(q), 0);
  }

  function wireForms() {
    // Capture phase, so this runs before the theme's own click handler on the
    // popup's button, which calls form.submit() directly and would skip it.
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('form[action="/search"] button[type="submit"]');
      if (btn) {
        e.preventDefault(); e.stopPropagation();
        var inp = btn.form && btn.form.querySelector('input[name="q"]');
        go(inp && inp.value);
        return;
      }
      var ps = e.target.closest && e.target.closest('[data-ps-item]');
      if (ps) {
        e.preventDefault(); e.stopPropagation();
        go(ps.dataset.psQuery || ps.textContent);
      }
    }, true);
    document.addEventListener('submit', function (e) {
      var f = e.target;
      if (!f || f.getAttribute('action') !== '/search') return;
      var inp = f.querySelector('input[name="q"]');
      e.preventDefault();
      go(inp && inp.value);
    }, true);
  }

  /* ── The results page ────────────────────────────────────────────────────── */

  function wireResultsPage() {
    var host = document.getElementById('jp-search-intent');
    if (!host) return;
    var sp = new URLSearchParams(location.search);
    var q = sp.get('q') || '';
    if (!q) return;
    // jp=off: the visitor removed a filter or asked for the words as typed —
    // no rewriting and no widening, but whatever filters remain still get chips.
    var off = sp.get('jp') === 'off';
    var level = off ? 99 : parseInt(sp.get('jp') || '0', 10) - 1;   // -1: not been through here

    // Shopify's pagination links carry the filters but drop `jp`, so page 2 of
    // a shape search arrives looking like an address that has never been
    // through here. Left alone it was rewritten back to page 1. The filters in
    // the address say which strictness it is already at.
    if (level < 0) {
      if (sp.has('filter.p.m.jewelry.shaped_stones')) level = 0;
      else if (sp.has('filter.p.m.jewelry.stone_shape')) level = 1;
      else if (sp.has('filter.p.product_type')) level = 2;
    }

    // Arrived without the parser (a bookmark, Google, an old link): send it
    // through once so the shape filter and spelling apply.
    if (level < 0) {
      var p0 = parse(q);
      if (p0.shapes.length || p0.items.length || p0.corrected) {
        // Keep the page the visitor is on: a rewrite that silently returns
        // them to page 1 is the same bug from the other direction.
        location.replace(resultsUrl(p0, 0, parseInt(sp.get('page') || '1', 10)));
        return;
      }
      return;
    }

    var raw = sp.get('jp_q') || q;
    var p = parse(raw);
    var count = parseInt(host.dataset.count || '0', 10);
    // Filter parameters Shopify actually applied (listed by page-search.liquid).
    var active = (host.dataset.active || '').split(',').filter(Boolean);

    // Nothing at this strictness: widen to the next level that changes
    // something. A plain search with no filters and no results stays as it is.
    if (count === 0 && !off) {
      var nx = nextLevel(p, level);
      if (nx >= 0) { location.replace(resultsUrl(p, nx)); return; }
    }

    // One chip per filter Shopify applied, read from the address itself so the
    // chips stay right after the visitor has removed one.
    var bits = [];
    [['filter.p.m.jewelry.shaped_stones', 'Shape'], ['filter.p.m.jewelry.stone_shape', 'Shape'],
     ['filter.p.product_type', 'Type']].forEach(function (kv) {
      var key = kv[0], vals = sp.getAll(key);
      if (!vals.length || active.indexOf(key) === -1) return;
      var label;
      if (key === 'filter.p.m.jewelry.shaped_stones' && p.label && vals.length > 1) {
        label = p.label;                 // "Pear Sapphire", not five colours
      } else if (key === 'filter.p.product_type') {
        label = vals.filter(function (v) { return v !== 'Earring'; }).join(', ');
      } else {
        label = vals.join(', ');
      }
      var rm = new URLSearchParams(location.search);
      rm.delete(key); rm.set('jp', 'off'); rm.delete('jp_q');
      bits.push('<a class="jp-ss-chip" href="/search?' + esc(rm.toString()) + '" title="Remove">' +
        '<span class="jp-ss-chip__k">' + kv[1] + '</span> ' + esc(label) + ' <span aria-hidden="true">&times;</span></a>');
    });
    var rn = off ? '' : relaxNote(p, level, count);
    if (rn) bits.push('<span class="jp-ss-msg">' + esc(rn) + '</span>');
    if (p.corrected && sp.get('jp_q')) {
      var asTyped = new URLSearchParams();
      asTyped.set('q', raw); asTyped.set('type', 'product'); asTyped.set('jp', 'off');
      bits.push('<span class="jp-ss-msg">Showing results for <em>' + esc(p.text) + '</em>. ' +
        '<a href="/search?' + esc(asTyped.toString()) + '">Search for &ldquo;' + esc(raw) + '&rdquo; instead</a></span>');
    }
    if (bits.length) {
      host.innerHTML = bits.join('');
      host.hidden = false;
    }
  }

  function init() { wirePopup(); wireForms(); wireResultsPage(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
